import React, { Component, createRef } from "react";
import axios from "../../utils/axiosInstance";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper/modules";

import "swiper/css";
import "swiper/css/navigation";
import "./ChatDashboard.css";

class ChatDashboard extends Component {
  constructor(props) {
    super(props);

    this.state = {
      conversations: [],
      activeChatId: null,
      messages: [],
      host_id: "",
      inputText: "",
      isLoading: true,
      isSending: false,
      loadingMessages: false,
      conversationsError: "",
      messagesError: "",
      sendError: "",
      mobileView: "inbox",
      showDetailsModal: false,
      showGuestDetailsModal: false,
    };

    this.dashboardRef = createRef();
  }

  componentDidMount() {
    this.getConversations();
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevState.messages !== this.state.messages) {
      this.scrollToBottom();
    }
  }

  scrollToBottom = () => {
    const dashboard = this.dashboardRef.current;
    if (!dashboard) return;

    // Desktop and mobile markup both exist in the DOM; scroll only the visible thread.
    const scroller = Array.from(
      dashboard.querySelectorAll(".messages-scroll-area")
    ).find((element) => element.getClientRects().length > 0);

    if (scroller) {
      scroller.scrollTo({ top: scroller.scrollHeight, behavior: "smooth" });
    }
  };

  getConversations = async () => {
    const customerId = localStorage.getItem("web_user_id");

    this.setState({ isLoading: true, conversationsError: "" });

    if (!customerId) {
      this.setState({
        conversations: [],
        activeChatId: null,
        host_id: "",
        messages: [],
        isLoading: false,
        loadingMessages: false,
        conversationsError: "Please sign in to view your conversations.",
        mobileView: "inbox",
      });
      return;
    }

    try {
      const response = await axios.get(`/chat/conversations/${customerId}`);
      const responseData = response?.data;
      const rows = Array.isArray(responseData?.data)
        ? responseData.data
        : Array.isArray(responseData)
          ? responseData
          : [];

      const conversations = rows.filter(
        (item) => item && item.id !== undefined && item.id !== null
      );

      if (conversations.length === 0) {
        this.setState({
          conversations: [],
          activeChatId: null,
          host_id: "",
          messages: [],
          isLoading: false,
          loadingMessages: false,
          conversationsError: "",
          messagesError: "",
          mobileView: "inbox",
        });
        return;
      }

      const firstConversation = conversations[0];
      this.setState(
        {
          conversations,
          activeChatId: firstConversation.id,
          host_id: firstConversation.host_id || "",
          messages: [],
          isLoading: false,
          loadingMessages: false,
          conversationsError: "",
          messagesError: "",
          mobileView: "inbox",
        },
        () => this.getMessages(firstConversation.id)
      );
    } catch (error) {
      const isNotFound = error?.response?.status === 404;
      if (!isNotFound) {
        console.error("Unable to load chat conversations:", error);
      }

      this.setState({
        conversations: [],
        activeChatId: null,
        host_id: "",
        messages: [],
        isLoading: false,
        loadingMessages: false,
        conversationsError: isNotFound
          ? ""
          : "We couldn't load your conversations. Check your connection and try again.",
        messagesError: "",
        mobileView: "inbox",
      });
    }
  };

  getMessages = async (chatId) => {
    if (chatId === undefined || chatId === null || chatId === "") {
      this.setState({ messages: [], loadingMessages: false, messagesError: "" });
      return;
    }

    const customerId = localStorage.getItem("web_user_id");
    if (!customerId) {
      this.setState({
        messages: [],
        loadingMessages: false,
        messagesError: "Please sign in again to view these messages.",
      });
      return;
    }

    this.setState({ loadingMessages: true, messagesError: "", sendError: "" });

    try {
      const response = await axios.get(`/chat/messages/${customerId}/${chatId}`);

      // Ignore an older request if the user has already opened another chat.
      if (String(this.state.activeChatId) !== String(chatId)) return;

      const responseData = response?.data;
      const messages = Array.isArray(responseData?.data)
        ? responseData.data
        : Array.isArray(responseData)
          ? responseData
          : [];

      this.setState({ messages, loadingMessages: false, messagesError: "" });
    } catch (error) {
      if (String(this.state.activeChatId) !== String(chatId)) return;

      const isNotFound = error?.response?.status === 404;
      if (!isNotFound) {
        console.error("Unable to load chat messages:", error);
      }

      this.setState({
        messages: [],
        loadingMessages: false,
        messagesError: isNotFound
          ? ""
          : "We couldn't load these messages. Please try again.",
      });
    }
  };

  sendMessage = async (event) => {
    if (event?.preventDefault) event.preventDefault();

    const { host_id, inputText, messages, isSending, activeChatId } = this.state;
    const messageText = inputText.trim();

    if (
      !messageText ||
      isSending ||
      activeChatId === null ||
      activeChatId === undefined ||
      !host_id
    ) {
      return;
    }

    const customerId = localStorage.getItem("web_user_id");
    if (!customerId) {
      this.setState({ sendError: "Please sign in again before sending a message." });
      return;
    }

    const messageId = `local-${Date.now()}`;
    const optimisticMessage = {
      id: messageId,
      sender: "user",
      message: messageText,
      time: new Date().toLocaleTimeString([], {
        hour: "numeric",
        minute: "2-digit",
      }),
    };

    this.setState(
      {
        messages: [...messages, optimisticMessage],
        inputText: "",
        isSending: true,
        sendError: "",
      },
      this.resizeChatInput
    );

    try {
      await axios.post("/chat/send", {
        host_id,
        customer_id: customerId,
        message: messageText,
      });

      if (String(this.state.activeChatId) === String(activeChatId)) {
        this.setState({ isSending: false, sendError: "" });
      }
    } catch (error) {
      console.error("Unable to send chat message:", error);

      if (String(this.state.activeChatId) === String(activeChatId)) {
        this.setState((currentState) => ({
          messages: currentState.messages.filter((item) => item.id !== messageId),
          inputText: currentState.inputText || messageText,
          isSending: false,
          sendError: "Your message couldn't be sent. Please try again.",
        }));
      }
    }
  };

  handleKeyDown = (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      this.sendMessage();
    }
  };

  handleInputChange = (event) => {
    const inputText = event.target.value;
    this.setState({ inputText }, this.resizeChatInput);
  };

  resizeChatInput = () => {
    const dashboard = this.dashboardRef.current;
    if (!dashboard) return;

    // Select the textarea in the visible desktop/mobile layout.
    const input = Array.from(dashboard.querySelectorAll(".chat-input")).find(
      (element) => element.getClientRects().length > 0
    );
    if (!input) return;

    input.style.height = "auto";
    const maxHeight = 112;
    const nextHeight = Math.min(input.scrollHeight, maxHeight);
    input.style.height = `${Math.max(42, nextHeight)}px`;
    input.style.overflowY = input.scrollHeight > maxHeight ? "auto" : "hidden";
  };

  handleChatChange = (chatId) => {
    const selectedChat = this.state.conversations.find(
      (chat) => String(chat.id) === String(chatId)
    );
    if (!selectedChat || String(this.state.activeChatId) === String(chatId)) {
      this.setState({ mobileView: "chat" });
      return;
    }

    this.setState(
      {
        activeChatId: selectedChat.id,
        messages: [],
        host_id: selectedChat.host_id || "",
        loadingMessages: false,
        messagesError: "",
        sendError: "",
        isSending: false,
        inputText: "",
        mobileView: "chat",
      },
      () => {
        this.resizeChatInput();
        this.getMessages(selectedChat.id);
      }
    );
  };

  handleMobileBack = () => {
    this.setState({ mobileView: "inbox" });
  };

  handlePayment = (bookingId) => {
    if (!bookingId) return;
    window.location.href = `/payment/${bookingId}`;
  };

  parseGallery = (gallery) => {
    if (!gallery) return [];
    if (Array.isArray(gallery)) return gallery;

    try {
      const parsed = JSON.parse(gallery);
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      return [];
    }
  };

  getBaseUrl = () => {
    const baseUrl = axios.defaults.baseURL || "";
    return baseUrl.replace(/\/api\/?$/, "");
  };

  getImageUrl = (image) => {
    if (typeof image !== "string" || !image.trim()) return "";
    if (/^(https?:)?\/\//i.test(image)) return image;
    return `${this.getBaseUrl()}/uploads/posts/${image.replace(/^\//, "")}`;
  };

  getInitials = (name) => {
    const words = String(name || "Host")
      .trim()
      .split(/\s+/)
      .filter(Boolean);
    return words.slice(0, 2).map((word) => word.charAt(0).toUpperCase()).join("") || "H";
  };

  isTruthy = (value) => {
    return value === true || value === 1 || ["1", "true", "yes"].includes(
      String(value ?? "").trim().toLowerCase()
    );
  };

  getTripDateLabel = (activeChat = {}) => {
    if (activeChat.date) return activeChat.date;
    if (activeChat.checkin_date && activeChat.checkout_date) {
      return `${activeChat.checkin_date} - ${activeChat.checkout_date}`;
    }
    return activeChat.checkin_date || activeChat.checkout_date || "Dates not set";
  };

  renderIcon = (type) => {
    const icons = {
      back: (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M15 18l-6-6 6-6" />
        </svg>
      ),
      send: (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M22 2 11 13" />
          <path d="m22 2-7 20-4-9-9-4Z" />
        </svg>
      ),
      call: (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
        </svg>
      ),
    };

    return icons[type] || null;
  };

  parseAmount = (value) => {
    if (typeof value === "number") {
      return Number.isFinite(value) ? value : 0;
    }

    if (value === null || value === undefined || String(value).trim() === "") {
      return 0;
    }

    const normalized = String(value).replace(/,/g, "");
    const match = normalized.match(/[-+]?(?:\d+\.?\d*|\.\d+)/);
    if (!match) return 0;

    const amount = Number(match[0]);
    return Number.isFinite(amount) ? amount : 0;
  };

  roundMoney = (value) => {
    const amount = Number(value);
    if (!Number.isFinite(amount)) return 0;
    return Math.round((amount + Number.EPSILON) * 100) / 100;
  };

  formatAmount = (value) =>
    this.roundMoney(value).toLocaleString("en-BD", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  parseDateValue = (value) => {
    if (!value) return null;

    const match = String(value).trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!match) return null;

    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);
    const timestamp = Date.UTC(year, month - 1, day);
    const parsed = new Date(timestamp);

    if (
      parsed.getUTCFullYear() !== year ||
      parsed.getUTCMonth() !== month - 1 ||
      parsed.getUTCDate() !== day
    ) {
      return null;
    }

    return timestamp;
  };

  getTotalNights = (activeChat = {}) => {
    const checkIn = this.parseDateValue(activeChat.checkin_date);
    const checkOut = this.parseDateValue(activeChat.checkout_date);

    if (checkIn !== null && checkOut !== null) {
      const dateDifference = Math.round((checkOut - checkIn) / 86400000);
      return Math.max(1, dateDifference);
    }

    const apiNights = this.parseAmount(activeChat.total_nights ?? activeChat.nights);
    return Math.max(1, Math.floor(apiNights || 1));
  };

  getPricingDetails = (activeChat = {}) => {
    const firstAvailable = (...values) =>
      values.find(
        (value) => value !== null && value !== undefined && String(value).trim() !== ""
      );

    const nights = this.getTotalNights(activeChat);
    const rawPerDay = firstAvailable(
      activeChat.per_day_amount,
      activeChat.price_per_day,
      activeChat.per_day_price
    );
    const rawTotal = firstAvailable(
      activeChat.total,
      activeChat.total_amount,
      activeChat.amount
    );

    let perDay = Math.max(0, this.roundMoney(this.parseAmount(rawPerDay)));
    let subTotal = perDay > 0
      ? this.roundMoney(perDay * nights)
      : Math.max(0, this.roundMoney(this.parseAmount(rawTotal)));

    // Use the returned total when a per-day price is not available.
    if (perDay <= 0 && subTotal > 0) {
      perDay = this.roundMoney(subTotal / nights);
    }

    const discount = Math.min(
      subTotal,
      Math.max(
        0,
        this.roundMoney(
          this.parseAmount(
            firstAvailable(activeChat.discount_amount, activeChat.discount, 0)
          )
        )
      )
    );

    const totalPayable = Math.max(0, this.roundMoney(subTotal - discount));
    const paymentStatus = String(activeChat.payment_status || "").trim().toLowerCase();
    const rawPaid = firstAvailable(
      activeChat.paid_amount,
      activeChat.amount_paid,
      activeChat.paid
    );
    const reportedPaid = Math.max(0, this.roundMoney(this.parseAmount(rawPaid)));
    const paid = Math.min(
      totalPayable,
      reportedPaid > 0 ? reportedPaid : paymentStatus === "paid" ? totalPayable : 0
    );
    const due = Math.max(0, this.roundMoney(totalPayable - paid));

    return { perDay, nights, discount, subTotal, totalPayable, paid, due };
  };

  getButtonProps = (activeChat = {}) => {
    const bookingStatus = String(activeChat.booking_status || "").trim().toLowerCase();
    const paymentStatus = String(activeChat.payment_status || "").trim().toLowerCase();
    const pricing = this.getPricingDetails(activeChat);
    const isPaid = paymentStatus === "paid" || (
      pricing.totalPayable > 0 && pricing.due === 0 && pricing.paid > 0
    );
    const isExpired = ["expired", "cancelled", "canceled"].includes(bookingStatus);

    if (isPaid || isExpired) {
      return {
        btnText: "Book again",
        btnDisabled: false,
        btnAction: () => {
          window.location.href = "/searchresultmobile";
        },
      };
    }

    if (bookingStatus === "confirmed" && activeChat.booking_id) {
      return {
        btnText: "Confirm And Pay",
        btnDisabled: false,
        btnAction: () => this.handlePayment(activeChat.booking_id),
      };
    }

    return {
      btnText: "Continue to Payment",
      btnDisabled: true,
      btnAction: null,
    };
  };

  renderConversationList = () => {
    const { conversations, activeChatId, isLoading, conversationsError } = this.state;

    if (isLoading) {
      return (
        <div className="chat-loading-list" role="status" aria-label="Loading conversations">
          {[1, 2, 3].map((item) => (
            <div className="chat-skeleton" key={item}>
              <div className="skeleton-avatar" />
              <div className="skeleton-content">
                <span />
                <span />
                <span />
              </div>
            </div>
          ))}
        </div>
      );
    }

    if (conversationsError) {
      return (
        <div className="chat-empty chat-error-state" role="alert">
          <div className="chat-empty-icon" aria-hidden="true">!</div>
          <h3>Couldn't load conversations</h3>
          <p className="chat-empty-description">{conversationsError}</p>
          <button type="button" className="chat-retry-button" onClick={this.getConversations}>
            Try again
          </button>
        </div>
      );
    }

    if (conversations.length === 0) {
      return (
        <div className="chat-empty" role="status">
          <div className="chat-empty-icon" aria-hidden="true">💬</div>
          <h3>No conversations yet</h3>
          <p className="chat-empty-description">
            Your conversations with property hosts will appear here.
          </p>
        </div>
      );
    }

    return conversations.map((chat) => {
      const isActive = String(activeChatId) === String(chat.id);
      const showReview = this.isTruthy(chat.reviewNeeded);

      return (
        <button
          type="button"
          key={chat.id}
          className={`conversation-item ${isActive ? "active" : ""}`}
          onClick={() => this.handleChatChange(chat.id)}
        >
          <div className="conversation-avatar">
            {chat.avatar ? (
              <img src={chat.avatar} alt={chat.host_name || "Host"} />
            ) : (
              <span className="avatar-fallback">{this.getInitials(chat.host_name)}</span>
            )}
            <span className={`online-status ${String(chat.status).toLowerCase() === "online" ? "online" : "offline"}`} />
          </div>

          <div className="conversation-content">
            <div className="conversation-top">
              <h3>{chat.host_name || "Property Host"}</h3>
              <span className="conversation-date">{chat.date || ""}</span>
            </div>
            <p className="conversation-message">{chat.last_message || "No messages yet"}</p>
            <div className="conversation-bottom">
              <span className="conversation-booking">{chat.title || "Property booking"}</span>
              {showReview && <span className="review-badge">Review</span>}
            </div>
          </div>
        </button>
      );
    });
  };

  renderNoActiveChat = () => {
    const { conversationsError, conversations, isLoading } = this.state;
    const hasError = Boolean(conversationsError);

    return (
      <div className="chat-no-selection" role={hasError ? "alert" : "status"}>
        <div className="chat-no-selection-icon" aria-hidden="true">
          {hasError ? "!" : "💬"}
        </div>
        <h2>
          {hasError
            ? "Your inbox is unavailable"
            : isLoading
              ? "Loading your inbox"
              : conversations.length > 0
                ? "Select a conversation"
                : "Your messages start here"}
        </h2>
        <p>
          {hasError
            ? conversationsError
            : isLoading
              ? "We're checking for your latest conversations."
              : conversations.length > 0
                ? "Choose a conversation to read messages and view its booking details."
                : "You don't have any conversations yet."}
        </p>
        {hasError && (
          <button type="button" className="chat-retry-button" onClick={this.getConversations}>
            Try again
          </button>
        )}
      </div>
    );
  };

  renderEmptyPropertyPanel = () => (
    <aside className="property-panel property-panel-empty">
      <div className="property-panel-header">
        <div>
          <span className="panel-eyebrow">BOOKING DETAILS</span>
          <h3>Property</h3>
        </div>
      </div>
      <div className="property-empty-state">
        <div className="property-empty-icon" aria-hidden="true">⌂</div>
        <h4>No booking selected</h4>
        <p>Property and payment information will appear when you open a conversation.</p>
      </div>
    </aside>
  );

  renderChatHeader = (activeChat) => {
    const isOnline = String(activeChat.status || "").toLowerCase() === "online";
    const phone = activeChat.phone || activeChat.host_phone;

    return (
      <div className="chat-screen-header">
        <button
          type="button"
          className="mobile-back-button"
          onClick={this.handleMobileBack}
          aria-label="Back to inbox"
        >
          {this.renderIcon("back")}
        </button>

        <div className="active-user-avatar">
          {activeChat.avatar ? (
            <img src={activeChat.avatar} alt={activeChat.host_name || "Host"} />
          ) : (
            <span className="avatar-fallback">{this.getInitials(activeChat.host_name)}</span>
          )}
          <span className={`active-online-dot ${isOnline ? "online" : "offline"}`} />
        </div>

        <div className="active-user-info">
          <h2>{activeChat.host_name || "Property Host"}</h2>
          <span className={isOnline ? "user-online" : "user-offline"}>
            <i />{isOnline ? "Online" : "Offline"}
          </span>
        </div>

        {phone && (
          <a href={`tel:${phone}`} className="chat-header-call" aria-label="Call host">
            {this.renderIcon("call")}
          </a>
        )}
      </div>
    );
  };

  renderMessages = (activeChat) => {
    const { messages, loadingMessages, isSending, messagesError } = this.state;

    if (loadingMessages) {
      return (
        <div className="messages-loading" role="status" aria-live="polite">
          <div className="message-loader">
            <span className="chat-spinner" /> Loading messages...
          </div>
        </div>
      );
    }

    if (messagesError && messages.length === 0) {
      return (
        <div className="messages-loading" role="alert">
          <div className="chat-message-state">
            <div className="chat-message-state-icon" aria-hidden="true">!</div>
            <h3>Messages unavailable</h3>
            <p>{messagesError}</p>
            <button
              type="button"
              className="chat-retry-button"
              onClick={() => this.getMessages(activeChat.id)}
            >
              Try again
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="messages-scroll-area">
        <div className="messages-list">
          {messages.length > 0 ? (
            messages.map((message, index) => {
              const isSent = String(message.sender || "").toLowerCase() === "user";
              return (
                <div
                  key={message.id ?? `message-${index}`}
                  className={`message-row ${isSent ? "sent" : "received"}`}
                >
                  <div className="message-bubble">
                    <div className="message-text">{message.message || ""}</div>
                    <div className="message-meta">
                      <span>{message.time || ""}</span>
                      {isSent && <span className="message-check">✓</span>}
                    </div>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="chat-no-messages" role="status">
              <div className="chat-no-messages-icon" aria-hidden="true">✉</div>
              <h3>No messages yet</h3>
              <p>Send a message to start the conversation.</p>
            </div>
          )}

          {isSending && <div className="message-send-status">Sending message...</div>}
        </div>
      </div>
    );
  };

  renderChatInput = () => {
    const { inputText, isSending, activeChatId } = this.state;

    return (
      <div className="chat-input-area">
        {this.state.sendError && (
          <div className="chat-send-error" role="alert">
            <span>{this.state.sendError}</span>
            <button
              type="button"
              onClick={() => this.setState({ sendError: "" })}
              aria-label="Dismiss error"
            >
              ×
            </button>
          </div>
        )}

        <form className="chat-input-wrapper" onSubmit={this.sendMessage}>
          <textarea
            className="chat-input"
            placeholder="Write a message... (Enter to send, Shift + Enter for a new line)"
            aria-label="Write a message"
            value={inputText}
            rows={1}
            onChange={this.handleInputChange}
            onKeyDown={this.handleKeyDown}
            disabled={!activeChatId}
          />
          <button
            type="submit"
            className="send-message-button"
            disabled={!inputText.trim() || isSending || !activeChatId}
            aria-label="Send message"
            title="Send message"
          >
            {this.renderIcon("send")}
          </button>
        </form>
      </div>
    );
  };

  renderModals = (activeChat) => {
    const { showDetailsModal, showGuestDetailsModal } = this.state;
    const { perDay, nights, discount, subTotal, totalPayable, paid, due } =
      this.getPricingDetails(activeChat);
    const { btnText, btnAction, btnDisabled } = this.getButtonProps(activeChat);

    return (
      <>
        {showDetailsModal && (
          <div
            className="custom-modal-overlay"
            onClick={() => this.setState({ showDetailsModal: false })}
          >
            <div className="custom-modal-content" onClick={(event) => event.stopPropagation()}>
              <div className="custom-modal-header">
                <h3>Booking Details</h3>
                <button
                  type="button"
                  onClick={() => this.setState({ showDetailsModal: false })}
                  aria-label="Close booking details"
                >
                  ×
                </button>
              </div>

              <div className="custom-modal-body details-scrollable">
                <div className="details-card">
                  <p className="details-title">{activeChat.title || "Property booking"}</p>

                  <section className="details-section">
                    <h4>Booking Details</h4>
                    <div className="details-row">
                      <span>Guests</span>
                      <strong>{activeChat.adults || 0} Adults</strong>
                    </div>
                    <div className="details-row">
                      <span>Check-in / Check-out</span>
                      <strong>{this.getTripDateLabel(activeChat)}</strong>
                    </div>
                    <div className="details-row">
                      <span>Status</span>
                      <strong>{activeChat.booking_status || "Pending"}</strong>
                    </div>
                  </section>

                  <section className="details-section mt-15">
                    <h4>Guest Details</h4>
                    <button
                      type="button"
                      className="modal-btn-secondary"
                      onClick={() => this.setState({
                        showDetailsModal: false,
                        showGuestDetailsModal: true,
                      })}
                    >
                      Show Guest Details
                    </button>
                  </section>

                  <section className="details-section mt-15">
                    <h4>Contact Details</h4>
                    <p className="details-note">
                      Contact number and location will be provided once the booking is confirmed and paid.
                    </p>
                  </section>

                  <section className="details-section mt-15">
                    <h4>Cancellation Policy</h4>
                    <p className="text-danger">Non Refundable</p>
                  </section>

                  <section className="details-section mt-15">
                    <h4>Price Details</h4>
                    <div className="details-row">
                      <span>BDT {this.formatAmount(perDay)} × {nights} {nights === 1 ? "day" : "days"}</span>
                      <strong className="price-amount">{this.formatAmount(subTotal)}</strong>
                    </div>
                    <div className="details-row">
                      <span>Discount</span>
                      <strong className="price-amount">{this.formatAmount(discount)}</strong>
                    </div>
                  </section>

                  <section className="details-section payment-summary-section mt-15">
                    <h4>Payment Summary</h4>
                    <div className="details-row">
                      <span>Total Payable</span>
                      <strong className="price-amount">{this.formatAmount(totalPayable)}</strong>
                    </div>
                    <div className="details-row">
                      <span>Paid</span>
                      <strong className="price-amount">{this.formatAmount(paid)}</strong>
                    </div>
                    <div className="details-row due-row">
                      <span>Due</span>
                      <strong className="price-amount">{this.formatAmount(due)}</strong>
                    </div>
                  </section>

                  <button
                    type="button"
                    className="modal-btn-primary mt-15"
                    disabled={btnDisabled}
                    onClick={btnAction || undefined}
                  >
                    {btnText}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {showGuestDetailsModal && (
          <div
            className="custom-modal-overlay"
            onClick={() => this.setState({ showGuestDetailsModal: false })}
          >
            <div
              className="custom-modal-content small-modal"
              onClick={(event) => event.stopPropagation()}
            >
              <div className="custom-modal-header">
                <h3>Guest Details</h3>
                <button
                  type="button"
                  onClick={() => this.setState({
                    showGuestDetailsModal: false,
                    showDetailsModal: true,
                  })}
                  aria-label="Back to booking details"
                >
                  ×
                </button>
              </div>

              <div className="custom-modal-body">
                <div className="guest-details-list">
                  <div className="details-row">
                    <span>Name</span>
                    <strong>{activeChat.customer_name || "N/A"}</strong>
                  </div>
                  <div className="details-row">
                    <span>Phone</span>
                    <strong>{activeChat.customer_phone || "N/A"}</strong>
                  </div>
                  <div className="details-row">
                    <span>Email</span>
                    <strong className="break-word">{activeChat.customer_email || "N/A"}</strong>
                  </div>
                </div>

                <button
                  type="button"
                  className="modal-btn-secondary mt-15"
                  onClick={() => this.setState({
                    showGuestDetailsModal: false,
                    showDetailsModal: true,
                  })}
                >
                  Back to Details
                </button>
              </div>
            </div>
          </div>
        )}
      </>
    );
  };

  renderPropertyDetails = (activeChat) => {
    const gallery = this.parseGallery(activeChat.gallery_images);
    const images = [];
    const featureImage = this.getImageUrl(activeChat.feature_image);

    if (featureImage) images.push(featureImage);
    gallery.forEach((image) => {
      const imageUrl = this.getImageUrl(image);
      if (imageUrl && !images.includes(imageUrl)) images.push(imageUrl);
    });

    const { perDay, nights, discount, subTotal, totalPayable, paid, due } =
      this.getPricingDetails(activeChat);
    const { btnText, btnAction, btnDisabled } = this.getButtonProps(activeChat);

    return (
      <aside className="property-panel">
        <div className="property-panel-header">
          <div>
            <span className="panel-eyebrow">BOOKING DETAILS</span>
            <h3>Property</h3>
          </div>
          <button
            type="button"
            className="prop-details-btn-desktop"
            onClick={() => this.setState({ showDetailsModal: true })}
          >
            Details
          </button>
        </div>

        <div className="property-scroll">
          {images.length > 0 ? (
            <div className="property-gallery">
              <Swiper navigation modules={[Navigation]} spaceBetween={0} slidesPerView={1}>
                {images.map((image, index) => (
                  <SwiperSlide key={`${image}-${index}`}>
                    <img src={image} alt={`Property ${index + 1}`} />
                  </SwiperSlide>
                ))}
              </Swiper>
            </div>
          ) : (
            <div className="property-image-placeholder" aria-label="No property image">
              <span>⌂</span>
              <p>Property image unavailable</p>
            </div>
          )}

          <div className="property-info-card">
            <h3>{activeChat.title || "Property booking"}</h3>
            <div className="property-status-tag">
              Status: <strong>{activeChat.booking_status || "Pending"}</strong>
            </div>

            <section className="desktop-details-section">
              <h4>Trip Details</h4>
              <p><strong>Dates:</strong> {this.getTripDateLabel(activeChat)}</p>
              <p><strong>Guests:</strong> {activeChat.adults || 0} Adults</p>
              {activeChat.baths !== undefined && (
                <p><strong>Bathrooms:</strong> {activeChat.baths}</p>
              )}
            </section>

            <section className="desktop-details-section">
              <button
                type="button"
                className="desktop-inline-btn"
                onClick={() => this.setState({ showGuestDetailsModal: true })}
              >
                View Guest Details
              </button>
            </section>

            <section className="desktop-details-section">
              <h4>Contact Details</h4>
              <p className="text-muted">
                Contact number and location will be provided once the booking is confirmed and paid.
              </p>
            </section>

            <section className="desktop-details-section">
              <h4>Cancellation Policy</h4>
              <p className="text-danger">Non Refundable</p>
            </section>

            <section className="price-summary">
              <h4>Price Details</h4>
              <div className="price-row">
                <span>BDT {this.formatAmount(perDay)} × {nights} {nights === 1 ? "day" : "days"}</span>
                <strong className="price-amount">{this.formatAmount(subTotal)}</strong>
              </div>
              <div className="price-row">
                <span>Discount</span>
                <strong className="price-amount">{this.formatAmount(discount)}</strong>
              </div>
              <div className="price-row total">
                <span>Total Payable</span>
                <strong className="price-amount">{this.formatAmount(totalPayable)}</strong>
              </div>
              <div className="price-row">
                <span>Paid</span>
                <strong className="price-amount">{this.formatAmount(paid)}</strong>
              </div>
              <div className="price-row due">
                <span>Due</span>
                <strong className="price-amount">{this.formatAmount(due)}</strong>
              </div>
            </section>
          </div>
        </div>

        <div className="property-action-footer">
          <button
            type="button"
            className="property-payment-button"
            disabled={btnDisabled}
            onClick={btnAction || undefined}
          >
            {btnText}
          </button>
        </div>
      </aside>
    );
  };

  render() {
    const { conversations, activeChatId, mobileView } = this.state;
    const activeChat = conversations.find(
      (chat) => String(chat.id) === String(activeChatId)
    ) || {};
    const hasActiveChat = activeChat.id !== undefined && activeChat.id !== null;
    const featureImage = this.getImageUrl(activeChat.feature_image);
    const isConfirmed = String(activeChat.booking_status || "").toLowerCase() === "confirmed";
    const { btnText, btnAction, btnDisabled } = this.getButtonProps(activeChat);

    return (
      <div className="chat-dashboard" ref={this.dashboardRef}>
        {hasActiveChat && this.renderModals(activeChat)}

        <div className="chat-desktop-layout">
          <aside className="inbox-sidebar">
            <div className="inbox-header">
              <div>
                <span className="page-eyebrow">MESSAGES</span>
                <h1>Inbox</h1>
              </div>
              <div className="inbox-count">{conversations.length}</div>
            </div>
            <div className="conversation-list">{this.renderConversationList()}</div>
          </aside>

          <main className="chat-main-panel">
            {hasActiveChat ? (
              <>
                {this.renderChatHeader(activeChat)}
                <div className="messages-area">{this.renderMessages(activeChat)}</div>
                {this.renderChatInput()}
              </>
            ) : (
              this.renderNoActiveChat()
            )}
          </main>

          {hasActiveChat
            ? this.renderPropertyDetails(activeChat)
            : this.renderEmptyPropertyPanel()}
        </div>

        <div className="chat-mobile-layout">
          {mobileView === "inbox" || !hasActiveChat ? (
            <div className="mobile-inbox-screen">
              <div className="mobile-inbox-header">
                <div>
                  <span className="page-eyebrow">MESSAGES</span>
                  <h1>Inbox</h1>
                </div>
                <div className="mobile-inbox-count">{conversations.length}</div>
              </div>
              <div className="mobile-conversation-list">{this.renderConversationList()}</div>
            </div>
          ) : (
            <div className="mobile-chat-screen">
              {this.renderChatHeader(activeChat)}

              <div className="mobile-chat-property">
                {featureImage ? (
                  <img src={featureImage} alt="Property" className="mobile-prop-thumb" />
                ) : (
                  <div className="mobile-prop-thumb-placeholder" aria-hidden="true">⌂</div>
                )}

                <div className="mobile-prop-details">
                  <div className="mobile-prop-header">
                    <h4>{activeChat.title || "Property booking"}</h4>
                    <button
                      type="button"
                      className="prop-details-btn"
                      onClick={() => this.setState({ showDetailsModal: true })}
                    >
                      Details
                    </button>
                  </div>
                  <div className="mobile-prop-meta">
                    <span className={`status-badge ${isConfirmed ? "confirmed" : ""}`}>
                      {activeChat.booking_status || "Pending"}
                    </span>
                    <span>{this.getTripDateLabel(activeChat)}</span>
                    <span>{activeChat.adults ?? 0} Guests</span>
                  </div>
                </div>
              </div>

              <div className="mobile-messages-area">{this.renderMessages(activeChat)}</div>
              {this.renderChatInput()}

              <div className="mobile-payment-bar">
                <button
                  type="button"
                  disabled={btnDisabled}
                  onClick={btnAction || undefined}
                >
                  {btnText}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }
}

export default ChatDashboard;
